import fp from 'lodash/fp';


export type IgnoreDefinition = (obj: any) => boolean;

type ContainerBuild = {
    $ignore?: IgnoreDefinition[];
    [key: string]: any;
};

type Container = { [key: string]: any };
type Transformation = (obj: any) => any;
type Predicate = (obj: any) => boolean;
type ConditionalTransformation = [Predicate, Transformation];

export const container: Container = {};

export const build = ({
    $ignore = [],
    ...extensions
}: ContainerBuild): Container => {
    const newContainer: Container = {};

    const buildDependency = (key: string): any => {
        const mapper: Transformation = obj => {
            const transforms: ConditionalTransformation[] = [
                [fp.isNil, fp.identity],

                [fp.anyPass($ignore), fp.identity],

                [fp.isArray, fp.map(mapper)],
                [fp.isFunction, fp.identity],
                [fp.isSymbol, fp.identity],

                [fp.has('$external'), fp.get('$external')],
                [fp.has('$constructor'),
                    ({ $constructor, $dependencies }) => $constructor(
                        ...fp.map(buildDependency, $dependencies),
                    )],

                [fp.isObject, fp.mapValues(mapper)],

                [fp.constant(true), fp.identity],
            ];

            const transformed = fp.find(([check]) => check(obj), transforms);
            return transformed && transformed[1](obj);
        };

        if (key in newContainer)
            return newContainer[key];

        if (newContainer[key] === null)
            throw new Error(`cyclic dependency detected for key ${key}`);

        if (!(key in extensions)) {
            throw new Error(`missing dependency: ${key}.` +
                ' please check container build method');
        }

        newContainer[key] = null;
        newContainer[key] = mapper(extensions[key]);
        return newContainer[key];
    };

    Object.keys(extensions).forEach(buildDependency);

    return newContainer;
};

export const applyContainer = (newContainer: Container): Container =>
    Object.assign(container, newContainer);

export const external = (extension: any) => ({ $external: extension });

export type Prioritized<TType> = [TType, number][];

export const prioritizeExtensions =
    <(<TType>(extensions: Prioritized<TType>) => TType[])>
    fp.pipe(
        fp.sortBy(fp.getOr(0, '1')),
        fp.map('0'),
    );

const merge = fp.mergeWith(
    (obj, src) => fp.isArray(obj)
        ? [...obj, ...src]
        : undefined,
);

export const mergeExtensions = (...extensions: object[]): object =>
    extensions.reduce(merge, {});
